import { lazy, Suspense } from "react"
import { createBrowserRouter } from "react-router-dom"

import { PageLoader } from "@/components/common/page-loader"
import { RouteErrorBoundary } from "@/components/common/route-error-boundary"
import { AppLayout } from "@/components/layouts/app-layout"
import { AuthLayout } from "@/features/auth/layouts/auth-layout"
import { ProtectedRoute } from "./protected-route"

// Carregamento "preguiçoso" (lazy loading) das páginas.
// O código de cada página só será baixado quando o usuário acessá-la.
const Dashboard = lazy(() =>
  import("@/features/dashboard/pages/dashboard").then((module) => ({
    default: module.Dashboard,
  })),
)
const Login = lazy(() =>
  import("@/features/auth/pages/login").then((module) => ({
    default: module.Login,
  })),
)
const CategoryList = lazy(() =>
  import("@/features/category/pages/category-list").then((module) => ({
    default: module.CategoryList,
  })),
)
const ClientsList = lazy(() =>
  import("@/features/clients/pages/clients-list").then((module) => ({ default: module.ClientsList })),
)
const DesignersList = lazy(() =>
  import("@/features/designers/pages/designers-list").then((module) => ({ default: module.DesignersList })),
)
const ProfessionalsList = lazy(() =>
  import("@/features/professionals/pages/professionals-list").then((module) => ({ default: module.ProfessionalsList })),
)
const ProjectsList = lazy(() =>
  import("@/features/projects/pages/projects-list").then((m) => ({ default: m.ProjectsList })),
)
const TablesList = lazy(() => import("@/features/tables/pages/tables-list").then((m) => ({ default: m.TablesList })))

const FurnituresList = lazy(() => import("@/features/furnitures/pages/furnitures-list").then((m) => ({ default: m.default })))

const BudgetsList = lazy(() => import("@/features/budgets/pages/budgets-list").then((m) => ({ default: m.BudgetsList })))

const BudgetFormPage = lazy(() => import("@/features/budgets/pages/budget-form").then((m) => ({ default: m.BudgetFormPage })))

const BudgetPrintPage = lazy(() => import("@/features/budgets/pages/budget-print").then((m) => ({ default: m.BudgetPrintPage })))

const UsersList = lazy(() => import("@/features/users/pages/users-list").then((m) => ({ default: m.UsersList })))

const CarriersList = lazy(() => import("@/features/carriers/pages/carriers-list").then((m) => ({ default: m.CarriersList })))

const FactoriesList = lazy(() =>
  import("@/features/factories/pages/factories-list").then((m) => ({ default: m.FactoriesList })),
)

export const router = createBrowserRouter([
  {
    path: "/",
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: "/",
            element: (
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: "/categories",
            element: (
              <Suspense fallback={<PageLoader />}>
                <CategoryList />
              </Suspense>
            ),
          },
          {
            path: "/clients",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ClientsList />
              </Suspense>
            ),
          },
          {
            path: "/designers",
            element: (
              <Suspense fallback={<PageLoader />}>
                <DesignersList />
              </Suspense>
            ),
          },
          {
            path: "/professionals",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ProfessionalsList />
              </Suspense>
            ),
          },
          {
            path: "/projects",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ProjectsList />
              </Suspense>
            ),
          },
          {
            path: "/tables",
            element: (
              <Suspense fallback={<PageLoader />}>
                <TablesList />
              </Suspense>
            ),
          },
          {
            path: "/furnitures",
            element: (
              <Suspense fallback={<PageLoader />}>
                <FurnituresList />
              </Suspense>
            ),
          },
          {
            path: "/budgets",
            element: (
              <Suspense fallback={<PageLoader />}>
                <BudgetsList />
              </Suspense>
            ),
          },
          {
            path: "/budgets/new",
            element: (
              <Suspense fallback={<PageLoader />}>
                <BudgetFormPage />
              </Suspense>
            ),
          },
          {
            path: "/budgets/:id",
            element: (
              <Suspense fallback={<PageLoader />}>
                <BudgetFormPage />
              </Suspense>
            ),
          },
          {
            path: "/budgets/:id/clone",
            element: (
              <Suspense fallback={<PageLoader />}>
                <BudgetFormPage />
              </Suspense>
            ),
          },
          {
            path: "/users",
            element: (
              <Suspense fallback={<PageLoader />}>
                <UsersList />
              </Suspense>
            ),
          },
          {
            path: "/carriers",
            element: (
              <Suspense fallback={<PageLoader />}>
                <CarriersList />
              </Suspense>
            ),
          },
          {
            path: "/factories",
            element: (
              <Suspense fallback={<PageLoader />}>
                <FactoriesList />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "/budgets/:id/print",
        element: (
          <Suspense fallback={<PageLoader />}>
            <BudgetPrintPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "/login",
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/login",
        element: <Suspense fallback={<PageLoader />}><Login /></Suspense>,
      },
    ], // A página de login não precisa de Suspense, pois o layout já é simples.
  },
])